import { movies as rawMovies } from "@/mocks/movies";

export interface Episode {
  ep: number;
  title: string;
  duration: string;
  synopsis: string;
}

export interface Movie {
  id: string;
  title: string;
  type: string;
  region: string;
  year: number;
  genres: string[];
  rating: number;
  views: number;
  status: string;
  updateDate: string;
  summary: string;
  slogan?: string;
  poster: string;
  backdrop?: string;
  director: string;
  cast: string[];
  episodes: Episode[];
}

export const movies = rawMovies as Movie[];

export function getAllMovies(): Movie[] {
  return movies;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function getFeatured(limit = 4): Movie[] {
  return movies.filter((movie) => Boolean(movie.backdrop)).slice(0, limit);
}

export function getLatestMovies(limit = 8): Movie[] {
  return [...movies]
    .sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1))
    .slice(0, limit);
}

export function getPopularMovies(limit = 8): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const genreScore = item.genres.filter((g) => movie.genres.includes(g)).length;
      const castScore = item.cast.filter((c) => movie.cast.includes(c)).length;
      const typeScore = item.type === movie.type ? 1 : 0;
      return { item, score: genreScore * 3 + castScore * 2 + typeScore };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export interface FilterOptions {
  type?: string;
  region?: string;
  year?: string;
  genre?: string;
  sort?: string;
}

export function filterMovies(options: FilterOptions): Movie[] {
  const { type, region, year, genre, sort } = options;
  let result = movies.filter((movie) => {
    if (type && type !== "全部" && movie.type !== type) return false;
    if (region && region !== "全部" && movie.region !== region) return false;
    if (year && year !== "全部" && String(movie.year) !== year) return false;
    if (genre && genre !== "全部" && !movie.genres.includes(genre)) return false;
    return true;
  });

  if (sort === "hot") {
    result = [...result].sort((a, b) => b.views - a.views);
  } else if (sort === "rating") {
    result = [...result].sort((a, b) => b.rating - a.rating);
  } else {
    result = [...result].sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
  }

  return result;
}

export function searchMovies(keyword: string): Movie[] {
  const term = keyword.trim().toLowerCase();
  if (!term) return [];
  return movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.region,
      movie.type,
      movie.director,
      ...movie.genres,
      ...movie.cast,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  });
}

export function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)} 亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return String(views);
}

export function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year} 年 ${Number(month)} 月 ${Number(day)} 日`;
}